import type {MoonArcSample} from './detailView';

/**
 * Tonight's pass, as arithmetic.
 *
 * The rows above the chart already say the moon rises at 21:16 and culminates
 * at 04:05 at 49.2°; what they cannot say is that it spends the small hours
 * high in the south and is barely up when you are awake. That is the whole job
 * of the arc, and this is the half of it that is geometry.
 *
 * Driven by sampled altitude rather than by the rise/set pair, so there is no
 * polar branch: above about 61° the moon has days with no rise, no set, or
 * neither, and altitude is defined on all of them. Same argument the twilight
 * band won.
 *
 * Nothing here needs a locale and nothing here is a colour. The element tree,
 * the theme tints and the peak label's clamping stay in each app.
 */

/**
 * Room under the horizon for the part of the pass that is below it, and above
 * the peak for the culmination label.
 *
 * **Not the 14/12 these started as.** The lowest sample is 30 minutes outside
 * the pass, so it sits `sin(pi * 30 / span)` of the plot below the horizon. At
 * the 150 both apps draw at, 14 overshoots the frame for any pass shorter than
 * about 13.9 h — most of them at a mid latitude — and clips both tails of the
 * stroked curve against the frame edge; 26 keeps a pass down to roughly 6 h
 * inside. (Both figures are height-specific: at 120 the same constants give
 * 10.5 h and 4.1 h.)
 *
 * Shorter than that the tail can still touch the floor, and two real cases sit
 * below it: a southern-declination moon at 60-63 N is up about 2.7 h, and the
 * no-rise/no-set fallback window is a whole local day rather than the pass plus
 * two 30-minute margins this arithmetic assumes. Both clip. The alternative —
 * scaling the margin with the span — was rejected anyway: it makes the vertical
 * scale mean something different from one night to the next, which is worse
 * than a clipped tail on the rarest passes.
 *
 * `TOP_PADDING` is 24 because it has to hold the label's *lane* (see
 * `ARC_MARKER_RADIUS`) and then the label's own ascender above that: 7 + 6 for
 * the lane leaves 11 for a 10.5px glyph, which is a cap height and change. At
 * 12 the degree sign rode over the top edge of the frame. At 16 — which the
 * webapp shipped — the lane fits, but the baseline lands at 3 and the glyph is
 * clipped by the frame instead; the marker is not what would cover it.
 */
const BELOW = 26;
const TOP_PADDING = 24;

/**
 * The radius both apps draw the "now" marker at.
 *
 * A plot dimension, not a style: the marker rides the curve, so at culmination
 * it is centred exactly on `peak.y` and covers everything within this distance
 * of it — including the label, which is how a moon at its highest point ended
 * up with its altitude hidden behind its own disc on both apps at once.
 * Exported so the element trees draw the same number the lane is derived from
 * instead of two `7`s that can drift apart.
 */
export const ARC_MARKER_RADIUS = 7;

/** Breathing room between the top of the marker and the label's baseline. */
const LABEL_GAP = 6;

/** A pass that is up for minutes rather than hours still has to be visible. */
const MIN_WINDOW_WIDTH = 2;

export type MoonArcGeometryParams = {
  samples: MoonArcSample[];
  /** The above-horizon span, in the same minutes as `samples`. */
  upWindow: {from: number; to: number} | null;
  /** The culmination, in the same minutes as `samples`. */
  peakMinute: number | null;
  /** Where "now" falls in the window, or null when it is outside it. */
  nowMinute: number | null;
  /**
   * Measured by the caller — and it must be the width of the element the plot
   * fills, never that of a padded ancestor. React Native's `onLayout` reports
   * the *padding* box, so measuring the wrapper that carries a content inset
   * hands the plot twice that inset in extra width and it silently overflows
   * its card. (The DOM's `contentRect` does not have this trap, which is why
   * only mobile ever showed it.)
   */
  width: number;
  height: number;
  below?: number;
  topPadding?: number;
};

export type MoonArcGeometry = {
  /** The stroked pass. */
  line: string;
  /**
   * The same pass, clipped at the horizon and closed along it.
   *
   * The fill stops at the horizon rather than following the curve below it:
   * filling to the frame's floor would shade the hours the moon is down as
   * though they were part of the pass, which is the one thing the chart is
   * there to distinguish.
   */
  area: string;
  /** y of the horizon line. */
  horizon: number;
  upWindow: {x: number; width: number} | null;
  /**
   * The culmination tick's foot; it runs from here down to the horizon.
   *
   * `labelY` is the baseline for the altitude printed above it, held one marker
   * radius plus a gap clear of `y` so that a marker sitting *on* the
   * culmination cannot cover it. Derived here rather than in each app so the
   * rule cannot be fixed on one side only — which is exactly what happened to
   * the regime guard this chart sits under (FE #171).
   */
  peak: {x: number; y: number; labelY: number} | null;
  /** The marker, placed on the sample nearest to now. */
  now: {x: number; y: number} | null;
};

export const altitudeArcGeometry = ({
  samples,
  upWindow,
  peakMinute,
  nowMinute,
  width,
  height,
  below = BELOW,
  topPadding = TOP_PADDING,
}: MoonArcGeometryParams): MoonArcGeometry | null => {
  // `height <= below + topPadding`, not `height <= 0`: below that the two
  // margins overlap and the plot turns inside out — the horizon rises above
  // the culmination and, at 40dp, the tick is drawn under the horizon it is
  // supposed to hang from. The threshold moved 26 -> 50 when those constants
  // did, and `MoonAltitudeArc` exposes `height` as a prop, so it is reachable.
  if (width <= 0 || height <= below + topPadding || samples.length < 2) {
    return null;
  }
  const first = samples[0].minute;
  const span = samples[samples.length - 1].minute - first;
  if (span <= 0) {
    return null;
  }

  // At least 1°, so a pass that never gets far off the horizon still fills the
  // frame instead of dividing by something near zero.
  const highest = Math.max(...samples.map((sample) => sample.altitude), 1);
  const horizon = height - below;

  const x = (minute: number) => ((minute - first) / span) * width;
  const y = (altitude: number) =>
    horizon - (altitude / highest) * (horizon - topPadding);

  const path = (altitudeOf: (sample: MoonArcSample) => number) =>
    samples
      .map(
        (sample, index) =>
          `${index === 0 ? 'M' : 'L'}${x(sample.minute).toFixed(1)},${y(
            altitudeOf(sample),
          ).toFixed(1)}`,
      )
      .join('');

  const clipped = path((sample) => Math.max(sample.altitude, 0));

  /**
   * The altitude of the sample nearest a minute, so a marker or a tick lands
   * *on* the drawn curve.
   *
   * An interpolated value floats off the line wherever the pass is steepest,
   * and — for the culmination — reading `highest` instead detaches the tick
   * entirely whenever the 1-degree floor below is doing any work: a pass
   * peaking at 0.2 degrees would draw its foot at the top of the frame with
   * 80px of dashed line above a curve that never gets there.
   */
  const altitudeNearest = (minute: number) =>
    samples.reduce((closest, sample) =>
      Math.abs(sample.minute - minute) < Math.abs(closest.minute - minute)
        ? sample
        : closest,
    ).altitude;

  return {
    line: path((sample) => sample.altitude),
    area: `${clipped}L${width},${horizon}L0,${horizon}Z`,
    horizon,
    upWindow:
      upWindow === null
        ? null
        : {
            x: x(upWindow.from),
            width: Math.max(
              x(upWindow.to) - x(upWindow.from),
              MIN_WINDOW_WIDTH,
            ),
          },
    peak:
      peakMinute === null
        ? null
        : {
            x: x(peakMinute),
            y: y(altitudeNearest(peakMinute)),
            labelY:
              y(altitudeNearest(peakMinute)) - ARC_MARKER_RADIUS - LABEL_GAP,
          },
    now:
      nowMinute === null
        ? null
        : {x: x(nowMinute), y: y(altitudeNearest(nowMinute))},
  };
};
